import { Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import Header from "./Header";
import Footer from "./Footer";

// ─── Reusable animation variants ───────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: i * 0.1 },
  }),
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i = 0) => ({
    opacity: 1,
    transition: { duration: 0.7, ease: "easeOut", delay: i * 0.08 },
  }),
};

const slideLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const slideRight = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: (i = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.55, ease: "easeOut", delay: i * 0.1 },
  }),
};

const openLoginModal = (e) => {
  e.preventDefault();

  const modalEl = document.getElementById("loginModal");

  if (modalEl && window.bootstrap?.Modal) {
    const modal = new window.bootstrap.Modal(modalEl);
    modal.show();
  }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

// ─── Hook: trigger animation once when element enters viewport ─────────────
function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: threshold });
  return [ref, isInView];
}

function Home() {
  const [courses, setCourses] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const scrollRef = useRef(null);
  const scroll = (ref, direction) =>
    ref.current.scrollBy({ left: direction * 350, behavior: "smooth" });

  useEffect(() => {
    fetch(
      "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Reviews/GetOrFilterReviews?courseId=&userId=",
    )
      .then((res) => res.json())
      .then(async (data) => {
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        const active = list.filter(
          (r) => r.status === "active" && r.review && r.rating >= 4,
        );
        // Fetch course names for each unique courseId
        const uniqueCourseIds = [
          ...new Set(active.map((r) => r.courseId).filter(Boolean)),
        ];
        const courseMap = {};
        await Promise.all(
          uniqueCourseIds.map((cid) =>
            fetch(
              `https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses/GetCourseById?courseId=${cid}`,
            )
              .then((r) => r.json())
              .then((d) => {
                if (d?.data?.title) courseMap[cid] = d.data.title;
              })
              .catch(() => {}),
          ),
        );
        const enriched = active.slice(0, 8).map((r) => ({
          ...r,
          courseName: courseMap[r.courseId] || "SLN Academy Course",
        }));
        setTestimonials(enriched);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses/GetAllCourses?limit=8&category",
        );
        if (!res.ok) throw new Error("Failed to fetch course");
        const data = await res.json();

        setCategory(data?.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, []);

  useEffect(() => {
    fetch(
      "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses/GetAllCourses?isActive=true",
    )
      .then((res) => res.json())
      .then((data) => {
        const all = data.data || [];
        setCourses(all.filter((c) => c.status === "published"));
      })
      .catch((err) => console.error("Courses API error:", err));
  }, []);

  const benefits = [
    {
      name: "Commit to Growth",
      image:
        "https://www.gettogetherfinance.com/frontend_asset/main_pages/images/feed.gif?v=1687199046",
      subtitle:
        "​The journey begins with a pivotal choice. Moving toward financial literacy requires a definitive Yes to self-improvement.",
    },
    {
      name: "Access the Blueprint",
      image:
        "https://www.gettogetherfinance.com/frontend_asset/main_pages/images/feed_second.gif?v=1687199046",
      subtitle:
        "​Once the commitment is made, the next step is securing the right resources.",
    },
    {
      name: "Interactive Immersion",
      image:
        "https://www.gettogetherfinance.com/frontend_asset/main_pages/images/feed_three.gif?v=1721394001",
      subtitle:
        "​Theory meets practice through live engagement. This stage is defined by active participation in digital workshops and direct mentorship.",
    },
    {
      name: "The Professional Evolution",
      image:
        "https://www.gettogetherfinance.com/frontend_asset/main_pages/images/feed_four.gif?v=1687199046",
      subtitle:
        "The final stage is the realization of your goals. Having moved from a novice perspective to a disciplined, professional mindset, you are now equipped with the tools and confidence to trade effectively.",
    },
  ];

  const createSlug = (text) => {
    return text
      ?.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");
  };

  const targetDate = new Date("2026-01-05T11:00:00");

  useEffect(() => {
    const timer = setTimeout(() => {
      const modalElement = document.getElementById("joinFree");
      if (modalElement) {
        const modal = new window.bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 20000000);

    return () => clearTimeout(timer);
  }, []);

  const [timeLeft, setTimeLeft] = useState({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = targetDate - now;

      if (difference <= 0) {
        clearInterval(timer);
        setTimeLeft({ days: "00", hours: "00", minutes: "00", seconds: "00" });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({
        days: String(days).padStart(2, "0"),
        hours: String(hours).padStart(2, "0"),
        minutes: String(minutes).padStart(2, "0"),
        seconds: String(seconds).padStart(2, "0"),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  // ── Section refs for scroll-based animations ──────────────────────────────
  const [bannerRef, bannerInView] = useScrollReveal(0.1);
  const [introRef, introInView] = useScrollReveal(0.15);
  const [superchargeRef, superchargeInView] = useScrollReveal(0.15);
  const [certsRef, certsInView] = useScrollReveal(0.2);
  const [whyRef, whyInView] = useScrollReveal(0.2);
  const [internshipRef, internshipInView] = useScrollReveal(0.1);
  const [masterclassRef, masterclassInView] = useScrollReveal(0.2);
  const [coursesRef, coursesInView] = useScrollReveal(0.1);
  const [founderRef, founderInView] = useScrollReveal(0.15);
  const [ceoRef, ceoInView] = useScrollReveal(0.15);
  const [partnersMarqueeRef, partnersMarqueeInView] = useScrollReveal(0.2);
  const [roadmapRef, roadmapInView] = useScrollReveal(0.1);
  const [videosRef, videosInView] = useScrollReveal(0.1);
  const [categoriesRef, categoriesInView] = useScrollReveal(0.1);
  const [statsRef, statsInView] = useScrollReveal(0.15);
  const [successRef, successInView] = useScrollReveal(0.15);
  const [testimonialRef, testimonialInView] = useScrollReveal(0.15);
  const [tabsRef, tabsInView] = useScrollReveal(0.1);
  const [missionRef, missionInView] = useScrollReveal(0.2);
  const [telegramRef, telegramInView] = useScrollReveal(0.2);
  const [institutionPartnersRef, institutionPartnersInView] =
    useScrollReveal(0.15);
  const [contactRef, contactInView] = useScrollReveal(0.1);

  return (
    <>
      <Header />

      {/* ── Modal ─────────────────────────────────────────────────────────── */}
      <div
        className="modal"
        id="joinFree"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabindex="-1"
      >
        <div className="modal-dialog modal-md">
          <motion.div
            className="modal-content rounded-5 border-0"
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <img
              src="/assets/images/home/3.jpg"
              className="w-100 object-fit-cover rounded-top-5"
              height="200"
            />
            <div className="modal-body ps-3 pe-0 pb-0">
              <div className="container pe-0">
                <div className="row g-0">
                  <div className="col-lg-7 col-12 pe-4">
                    <div>
                      <div className="countdown mb-2">
                        <ul className="list-inline m-0 mb-3">
                          {[
                            { label: "Days", val: timeLeft.days },
                            { label: "Hours", val: timeLeft.hours },
                            { label: "Minutes", val: timeLeft.minutes },
                            { label: "Seconds", val: timeLeft.seconds },
                          ].map(({ label, val }) => (
                            <li
                              key={label}
                              className="list-inline-item me-md-3 text-center"
                            >
                              <motion.span
                                key={val}
                                initial={{ scale: 1.3, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.25 }}
                                className="display-6 fw-bold text-primary d-block"
                              >
                                {val}
                              </motion.span>
                              <p className="fs-5 m-0 lh-1">{label}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <h3 className="text-dark ls-sm m-0 lh-sm">
                        Join Free 7-Day Masterclass 🎉
                      </h3>
                      <p className="m-0 h4 lh-sm text-dark fw-light opacity-75">
                        Seats are limited. Reserve your free seat now.
                      </p>
                    </div>
                    <div className="mb-2 mt-3">
                      <label className="label-text text-dark opacity-75 mb-1 ms-3">
                        Your Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control ps-3 fs-5 ls-sm rounded-pill"
                        placeholder="Enter Name"
                      />
                    </div>
                    <div className="mb-2">
                      <label className="label-text text-dark opacity-75 mb-1 ms-3">
                        Phone / WhatsApp No{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control ps-3 fs-5 ls-sm rounded-pill"
                        placeholder="Enter Phone / WhatsApp No."
                      />
                      <p className="smtext m-0 ms-3 me-5 mt-2 lh-1 text-secondary">
                        Enter a valid phone number. We'll verify it later — if
                        you have a WhatsApp number, you can use that instead.
                      </p>
                    </div>
                    <div className="d-flex align-items-center mt-4 mb-5">
                      <Link
                        to="/"
                        className="btn bg-gold text-dark fw-medium w-auto px-5 rounded-pill border-0"
                      >
                        Join Free Masterclass
                      </Link>
                      <button
                        type="button"
                        className="btn btn-success rounded-circle px-3 label-text text-white btn-close-modal ms-1"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                  <div className="col-lg-5 col-12 d-flex align-items-end">
                    <img
                      src="/assets/images/book.png"
                      className="w-85 rounded-top-circle rounded-end-5 rounded-top-right-0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Hero Carousel ─────────────────────────────────────────────────── */}
      <div
        id="heroBanner"
        className="carousel slide"
        data-bs-ride="carousel"
      >
        <div className="carousel-inner">
          <div className="carousel-item active">
            <Link to="/course/CREA830F/free-masterclass">
              <img
                src="/assets/images/banner/1.svg"
                alt="Image"
                className="w-100 object-fit-cover"
                height="370"
              />
            </Link>
          </div>
          <div className="carousel-item">
            <Link to="/sln-scan">
              <img
                src="/assets/images/banner/2.svg"
                alt="Image"
                className="w-100 object-fit-cover"
                height="370"
              />
            </Link>
          </div>
          <div className="carousel-item">
            <Link to="/category">
              <img
                src="/assets/images/banner/3.svg"
                alt="Image"
                className="w-100 object-fit-cover"
                height="370"
              />
            </Link>
          </div>
        </div>
        <button
          className="carousel-control-prev"
          type="button"
          data-bs-target="#heroBanner"
          data-bs-slide="prev"
        >
          <div className="button-md-circle">
            <i className="bi bi-chevron-left display-5 text-dark"></i>
          </div>
        </button>
        <button
          className="carousel-control-next"
          type="button"
          data-bs-target="#heroBanner"
          data-bs-slide="next"
        >
          <div className="button-md-circle">
            <i className="bi bi-chevron-right display-5 text-dark"></i>
          </div>
        </button>
      </div>

      {/* ── Intro Section ─────────────────────────────────────────────────── */}
      <section className="py-6 mb-2" ref={introRef}>
        <div className="container">
          <motion.div
            className="text-center"
            variants={fadeUp}
            initial="hidden"
            animate={introInView ? "visible" : "hidden"}
          >
            <h5 className="lh-1 mb-2 fs-3 text-danger fw-normal">
              <p className="fw-semibold text-dark mb-3 text-decoration-underline">
                EMPOWER YOUR KNOWLEDGE, ENRICH YOUR WEALTH AND BUILD YOUR CAREER
              </p>
              SLN Academy equips you with certified, practical stock market
              education to launch a successful career in finance.
              <br />
              Learn, grow, and succeed with us.
            </h5>
            <h2 className="h1 fw-normal ls-sm lh-1 mt-3">
              Best Stock Market Institute in India{" "}
              <span className="fw-bold text-decoration-underline">
                Swinglearn Network LLP
              </span>
            </h2>
            <div className="mt-n3 pt-2">
              <img
                src="/assets/images/line-shap.webp"
                width="230"
                className="mt-2"
              />
            </div>
          </motion.div>

          <div className="row mt-6">
            <motion.div
              className="col-lg-7 pe-8"
              variants={slideLeft}
              initial="hidden"
              animate={introInView ? "visible" : "hidden"}
            >
              <p className="lead fw-medium text-black opacity-75 lh-sm">
                Swinglearn Network LLP is a fintech education platform based in
                Prayagraj, Uttar Pradesh, dedicated to providing job-oriented
                stock market education. The company offers NISM/NCFM
                certification preparation, and a practical 3 & 6-months diploma
                with job assistance. Its hybrid model (online + recorded)
                ensures accessibility and scalability.
              </p>
              <p className="lead fw-medium text-black opacity-75 lh-sm mt-4">
                SLN Academy is supported by a dedicated team comprising of:
              </p>
              <p className="lead fw-medium text-black lh-sm">
                The team collectively focuses on delivering quality education,
                structured learning, and continuous student assistance across
                online and offline platforms.
              </p>
              <ul className="lead fw-medium text-black lh-sm mt-3">
                <li>Experienced Trainers</li>
                <li>Curriculum & Content Developers</li>
                <li>Student Support & Operations Team</li>
              </ul>
              <Link
                to="/about"
                className="btn btn-dark px-7 mt-3 rounded-pill mb-lg-0 mb-3"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
              >
                Know More About SLN
              </Link>
            </motion.div>
            <motion.div
              className="col-lg-5"
              variants={slideRight}
              initial="hidden"
              animate={introInView ? "visible" : "hidden"}
            >
              <iframe
                className="w-100 object-fit-cover rounded-5"
                height="315"
                src="https://www.youtube.com/embed/2xa1kphLoyw?si=_4yVQpUodhJgSsR-"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Supercharge Section ───────────────────────────────────────────── */}
      <section className="mb-6" ref={superchargeRef}>
        <div className="container">
          <div className="row">
            <motion.div
              className="rounded-5 py-6 gradient-section-1"
              variants={scaleIn}
              initial="hidden"
              animate={superchargeInView ? "visible" : "hidden"}
            >
              <div className="row ps-7 align-items-center">
                <motion.div
                  className="col-lg-6 col-md-12 col-12"
                  variants={slideLeft}
                  initial="hidden"
                  animate={superchargeInView ? "visible" : "hidden"}
                >
                  <div className="mb-5 mb-lg-0 pe-5">
                    <h2 className="display-5 fw-semibold text-white lh-sm ls-sm mb-3">
                      Get Ready to{" "}
                      <u className="text-warning fw-bold">
                        <span className="text-primary">Supercharge</span>{" "}
                      </u>
                      Your
                      <br />
                      Trading Journey !
                    </h2>
                    <p className="mb-5 lead fw-normal lh-sm ls-sm">
                      Get ready to supercharge your trading journey! Learn the
                      proven strategies and insider techniques that every
                      professional trader uses to succeed in the stock market.
                    </p>
                    <Link
                      to="/category"
                      className="btn btn-light px-5 rounded-pill"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.96 }}
                    >
                      Get Started
                    </Link>
                    <Link
                      to="/course/CREA830F/free-masterclass"
                      className="btn btn-outline-light px-5 rounded-pill ms-2"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.96 }}
                    >
                      Join Free Masterclass
                    </Link>
                  </div>
                </motion.div>
                <motion.div
                  className="col-lg-6 col-md-12 col-12 pe-0"
                  variants={slideRight}
                  initial="hidden"
                  animate={superchargeInView ? "visible" : "hidden"}
                >
                  <div className="d-flex justify-content-center align-items-center position-relative rounded border-white border-end-0 rounded-start-pill rounded-end-0 border border-5 bg-cover bg-image overflow-hidden">
                    <a
                      className="glightbox icon-shape rounded-circle btn-play icon-xl"
                      href="https://www.youtube.com/watch?v=Nfzi7034Kbg"
                    >
                      <i className="bi bi-play fs-1"></i>
                    </a>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Certificates Section ──────────────────────────────────────────── */}
      <section className="my-6" ref={certsRef}>
        <div className="container">
          <motion.div
            className="mb-5"
            variants={fadeUp}
            initial="hidden"
            animate={certsInView ? "visible" : "hidden"}
          >
            <div>
              <span className="text-uppercase text-secondary fw-semibold fs-4 ls-sm">
                OUR Certificates
              </span>
              <h2 className="h1 fw-semibold ls-sm text-gold lh-1 mt-1">
                Recognitions, Certifications &,
                <br />
                Achievements
              </h2>
              <div className="mt-n3 pt-2">
                <img
                  src="/assets/images/line-shap.webp"
                  width="230"
                  className="mt-2"
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="my-4 pb-3 d-flex gap-2 flex-wrap"
            variants={staggerContainer}
            initial="hidden"
            animate={certsInView ? "visible" : "hidden"}
          >
            {["iso", "msme", "startup-india", "nse"].map((cert, i) => (
              <motion.div
                key={cert}
                className="rounded-5 p-4 h-100 bg-light"
                variants={scaleIn}
                custom={i}
                whileHover={{
                  scale: 1.06,
                  boxShadow: "0 6px 24px rgba(0,0,0,0.10)",
                }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <img src={`/assets/images/${cert}.png`} height="60" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Why SLN Section ───────────────────────────────────────────────── */}
      <section className="py-7 gradient-section-2" ref={whyRef}>
        <div className="container">
          <motion.div
            className="row text-center"
            variants={fadeUp}
            initial="hidden"
            animate={whyInView ? "visible" : "hidden"}
          >
            <div className="col-md-12 px-lg-8">
              <span className="text-uppercase text-white fw-semibold ls-sm fs-4">
                More than 30,000 students
              </span>
              <h2 className="h1 fw-normal ls-sm text-dark lh-1 mt-1">
                Have Called It The Best Way to Learn Stock Market
              </h2>
            </div>
          </motion.div>
          <div className="d-flex justify-content-center align-items-center">
            <div className="col-9 mt-4">
              <motion.div
                className="row g-2 justify-content-center align-items-center"
                variants={staggerContainer}
                initial="hidden"
                animate={whyInView ? "visible" : "hidden"}
              >
                {[
                  {
                    icon: "bi-card-text",
                    title: "Certified & Credible",
                    sub: "ISO & MSME certified, recognized by Startup India.",
                  },
                  {
                    icon: "bi-tv",
                    title: "Practical Stock Market Learning",
                    sub: "Hands-on stock market training for real skills.",
                  },
                  {
                    icon: "bi-shield-check",
                    title: "Industry-Linked Internships",
                    sub: "Experience real-world trading with top broking firms.",
                  },
                  {
                    icon: "bi-wallet",
                    title: "Affordable & Accessible",
                    sub: "Quality financial education at pocket-friendly fees.",
                  },
                ].map(({ icon, title, sub }, i) => (
                  <motion.div
                    key={title}
                    className="col-lg-6 col-12"
                    variants={fadeUp}
                    custom={i}
                  >
                    <motion.div
                      className="pe-6 ps-2 py-2 rounded-pill bg-white"
                      whileHover={{
                        scale: 1.03,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <div className="icon-shape icon-xl bg-light-primary rounded-circle">
                          <i className={`bi ${icon} fs-3 text-primary`}></i>
                        </div>
                        <div className="ms-3">
                          <h3 className="mb-0 text-dark lh-1 fs-4">{title}</h3>
                          <span className="text-secondary">{sub}</span>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Internship Partners ───────────────────────────────────────────── */}
      <section ref={internshipRef}>
        <div className="container mt-8">
          <motion.div
            className="row text-center"
            variants={fadeUp}
            initial="hidden"
            animate={internshipInView ? "visible" : "hidden"}
          >
            <div className="col-md-12 px-lg-8">
              <span className="text-uppercase text-primary fw-semibold ls-sm fs-3 lh-1">
                Our Internship Partners
              </span>
              <h2 className="h1 fw-normal ls-sm text-dark lh-1 mt-1">
                Organizations Empowering Our Internship Programs
              </h2>
              <div className="mt-n2 pt-2">
                <img
                  src="/assets/images/line-shap.webp"
                  width="230"
                  className="mt-2"
                />
              </div>
            </div>
            <div className="position-relative mt-4 mb-2">
              <button
                onClick={() => scroll(scrollRef, -1)}
                className="position-absolute top-50 start-0 translate-middle-y z-1 btn btn-light rounded-circle shadow d-flex align-items-center justify-content-center"
                style={{ width: 44, height: 44 }}
              >
                <i className="bi bi-chevron-left"></i>
              </button>

              <div
                ref={scrollRef}
                className="d-flex gap-2 overflow-x-scroll"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                <img
                  src="/assets/images/internship/1.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="350"
                  height="350"
                />
                <img
                  src="/assets/images/internship/2.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="290"
                  height="350"
                />
                <img
                  src="/assets/images/internship/5.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="420"
                  height="350"
                />
                <img
                  src="/assets/images/internship/6.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="450"
                  height="350"
                />
                <img
                  src="/assets/images/internship/3.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="400"
                  height="350"
                />
                <img
                  src="/assets/images/internship/4.jpeg"
                  className="object-fit-cover rounded-5 border border-light"
                  width="400"
                  height="350"
                />
              </div>

              <button
                onClick={() => scroll(scrollRef, 1)}
                className="position-absolute top-50 end-0 translate-middle-y z-1 btn btn-light rounded-circle shadow d-flex align-items-center justify-content-center"
                style={{ width: 44, height: 44 }}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Masterclass CTA ───────────────────────────────────────────────── */}
      <section ref={masterclassRef}>
        <div className="container mt-7">
          <motion.div
            className="bg-info p-6 rounded-5"
            variants={scaleIn}
            initial="hidden"
            animate={masterclassInView ? "visible" : "hidden"}
          >
            <div className="container">
              <div className="row">
                <motion.div
                  className="col-lg-8"
                  variants={slideLeft}
                  initial="hidden"
                  animate={masterclassInView ? "visible" : "hidden"}
                >
                  <h5 className="mb-0 fs-3 fw-normal lh-sm text-light">
                    Free 7-Day Masterclass — Intraday & Option Chain Data
                    Trading
                  </h5>
                  <h2 className="h1 text-white fw-normal ls-sm lh-1">
                    Trusted By <span className="fw-bold">10K+</span> Users
                  </h2>
                  <div className="mt-n4 pt-2">
                    <img
                      src="/assets/images/line-shap.webp"
                      width="230"
                      className="mt-2"
                    />
                  </div>
                </motion.div>
                <motion.div
                  className="col-lg-4 d-flex justify-content-lg-end justify-content-center align-items-center"
                  variants={slideRight}
                  initial="hidden"
                  animate={masterclassInView ? "visible" : "hidden"}
                >
                  <motion.a
                    href="https://www.youtube.com/@SLNAcademyOfficial"
                    target="_blank"
                    className="btn btn-light btn-md px-7 rounded-4 mb-lg-0 mb-5"
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    Subscribe Now
                  </motion.a>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Courses Section ───────────────────────────────────────────────── */}
      <section className="pb-8 bg-white" ref={coursesRef}>
        <div className="container">
          <motion.div
            className="pt-7"
            variants={fadeUp}
            initial="hidden"
            animate={coursesInView ? "visible" : "hidden"}
          >
            <div className="col-12">
              <span className="badge bg-dark-soft rounded-pill fs-5 px-4">
                Our <span className="fw-bold">Courses</span>
              </span>
              <h2 className="fw-bold mb-0 text-dark mt-2">
                Stock market courses{" "}
                <u className="text-primary">
                  <span className="text-dark">for everyone</span>
                </u>
              </h2>
              <p className="lead lh-sm">
                Choose from a wide range of stock market courses on trading and
                investing.
              </p>
            </div>

            <motion.div
              className="row g-2 mt-3"
              variants={staggerContainer}
              initial="hidden"
              animate={coursesInView ? "visible" : "hidden"}
            >
              {courses.slice(0, 3).map((course, index) => (
                <div key={index} className="col-lg-4 col-12">
                  <motion.div
                    className="row g-0 shadow rounded-5"
                    variants={fadeUp}
                    custom={index}
                    whileHover={{
                      y: -6,
                      boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                    }}
                    transition={{ type: "spring", stiffness: 200 }}
                  >
                    <div className="col-12">
                      <Link
                        to={`/course/${course?.courseId}/${createSlug(course?.title)}`}
                      >
                        <div className="position-relative">
                          <img
                            src={`/assets/images/course-banner/${index + 1}.jpg`}
                            alt="Course"
                            className="w-100 object-fit-cover rounded-top-5"
                            height="220"
                          />

                          <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-75 rounded-top-5"></div>

                          <div className="position-absolute bottom-0 start-0 p-4 z-1 w-100">
                            <h3 className="mb-6 text-white lh-sm ls-sm text-center fw-bold text-shadow">
                              {course?.title}
                            </h3>
                            <span className="badge text-bg-danger fs-5 fst-italic px-4 rounded-pill mb-2">
                              By {course?.instructors[0]?.name}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </div>
                    <div className="col-12 px-4 d-flex align-items-center border pb-4 pt-1 border-1 rounded-bottom-4 border-top-0">
                      <div className="d-flex flex-column px-2 mb-2">
                        <div className="d-flex flex-column gap-3 mt-4">
                          <div className="d-flex flex-column">
                            <h3 className="mb-0 lh-1">
                              <Link
                                to={`/course/${course?.courseId}/${createSlug(course?.title)}`}
                                className="fs-3 text-dark"
                              >
                                {course?.category}
                              </Link>
                            </h3>
                            <p className="mb-0 lh-1 mt-1 mb-2">
                              {course?.about?.split(" ").slice(0, 15).join(" ")}
                              ...
                            </p>
                          </div>
                          <div className="lh-1">
                            <span className="text-secondary fw-semibold">
                              4.5
                            </span>
                            <span className="align-text-top ms-3 me-2">
                              {[...Array(5)].map((_, i) => (
                                <i
                                  key={i}
                                  className="bi bi-star-fill text-warning"
                                ></i>
                              ))}
                            </span>
                            <span className="text-gray-500">(13,245)</span>
                          </div>
                        </div>
                        <div className="d-flex flex-row gap-2 align-items-center">
                          <span className="fs-3 lh-1 mt-2 fw-semibold text-dark">
                            ₹{course?.offeredPrice} + GST
                          </span>
                        </div>
                        <span className="fw-medium lh-sm text-gray-400">
                          <del>
                            ₹{course?.originalPrice?.toLocaleString("en-IN")}
                          </del>
                        </span>
                        <div className="mt-3">
                          <motion.div
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                            style={{ display: "inline-block" }}
                          >
                            <Link
                              to={`/course/${course?.courseId}/${createSlug(course?.title)}`}
                              className="btn bg-gold text-dark w-auto px-5 rounded-pill border-0"
                            >
                              View Course
                            </Link>
                          </motion.div>
                          <Link className="btn btn-icon btn-success rounded-circle d-none d-md-inline-flex ms-1">
                            <i className="bi bi-whatsapp fs-4 align-middle"></i>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* ── Founder Section ─────────────────────────────────────────────── */}
        <section className="mt-6" ref={founderRef}>
          <div className="container">
            <motion.div
              className="bg-success-soft p-5 rounded-5"
              variants={scaleIn}
              initial="hidden"
              animate={founderInView ? "visible" : "hidden"}
            >
              <div className="row mt-3">
                <motion.div
                  className="col-lg-9 d-flex flex-column justify-content-center px-lg-7"
                  variants={slideLeft}
                  initial="hidden"
                  animate={founderInView ? "visible" : "hidden"}
                >
                  <div className="mb-3">
                    <span className="text-uppercase text-secondary fw-semibold fs-4 ls-sm">
                      Our Founder
                    </span>
                    <h2 className="h1 fw-semibold ls-sm text-gold lh-1">
                      Hi, I'm Raj Nath, Founder
                    </h2>
                    <div className="mt-n3 pt-2">
                      <img
                        src="/assets/images/line-shap.webp"
                        width="230"
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <p className="mb-3 text-black fs-4 lh-sm">
                    SLN Academy was founded with a clear vision — to provide
                    practical, career-oriented stock market education that truly
                    prepares learners for the real financial world. I believe
                    that the right knowledge and guidance can transform
                    beginners into confident traders and finance professionals.
                  </p>
                  <p className="mb-2 fs-4 lh-sm">
                    Through SLN Academy, our goal is to bridge the gap between
                    theoretical learning and real market experience. We focus on
                    building strong fundamentals, practical skills, and industry
                    exposure so that every student can step into the financial
                    market with confidence.
                  </p>
                  <p className="mb-2 fs-4 lh-sm">
                    Empower Your Knowledge, Enrich Your Wealth, and Build Your
                    Career with SLN Academy.
                  </p>
                  <p className="mb-2 fs-4 text-dark lh-sm">
                    Warm Regards,
                    <br />
                    Raj Nath
                    <br />
                    SLN Academy &nbsp;[Founder]
                  </p>
                </motion.div>
                <motion.div
                  className="col-lg-3 d-flex justify-content-center"
                  variants={slideRight}
                  initial="hidden"
                  animate={founderInView ? "visible" : "hidden"}
                  whileHover={{ scale: 1.03 }}
                >
                  <img
                    src="/assets/images/director.png"
                    className="object-fit-contain rounded-5"
                    height="350"
                  />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── CEO Section ─────────────────────────────────────────────────── */}
        <section className="mb-6 mt-1" ref={ceoRef}>
          <div className="container">
            <motion.div
              className="bg-danger-soft p-5 rounded-5"
              variants={scaleIn}
              initial="hidden"
              animate={ceoInView ? "visible" : "hidden"}
            >
              <div className="row mt-3">
                <motion.div
                  className="col-lg-3 d-flex justify-content-center"
                  variants={slideLeft}
                  initial="hidden"
                  animate={ceoInView ? "visible" : "hidden"}
                  whileHover={{ scale: 1.03 }}
                >
                  <img
                    src="/assets/images/ceo.png"
                    className="object-fit-contain rounded-5"
                    height="350"
                  />
                </motion.div>
                <motion.div
                  className="col-lg-9 d-flex flex-column justify-content-center px-lg-8"
                  variants={slideRight}
                  initial="hidden"
                  animate={ceoInView ? "visible" : "hidden"}
                >
                  <div className="mb-3 mt-lg-0 mt-4">
                    <span className="text-uppercase text-secondary fw-semibold fs-4 ls-sm">
                      Our Commitment to Excellence in Financial Education
                    </span>
                    <h2 className="h1 fw-semibold ls-sm text-gold lh-1">
                      Message from the CEO
                    </h2>
                    <div className="mt-n3 pt-2">
                      <img
                        src="/assets/images/line-shap.webp"
                        width="230"
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <p className="mb-3 text-black fs-4 lh-sm">
                    At SLN Academy, our vision is simple yet powerful — to make
                    quality stock market education accessible, practical, and
                    career-oriented for every aspiring learner in India.
                  </p>
                  <p className="mb-2 fs-4 lh-sm">
                    The financial market is full of opportunities, but success
                    comes with the right knowledge, discipline, and guidance. At
                    SLN Academy, we focus not just on theory, but on real-world
                    application, live market training, and industry exposure
                    that prepares our students to confidently step into the
                    trading and investment world.
                  </p>
                  <p className="mb-2 fs-4 lh-sm">
                    Thank you for being a part of the SLN Academy journey.
                  </p>
                  <p className="mb-2 fs-4 text-dark lh-sm">
                    Warm Regards,
                    <br />
                    Apurv Kumar Pathak
                    <br />
                    SLN Academy &nbsp;[CEO]
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── Marquee Partners ────────────────────────────────────────────── */}
        <section ref={partnersMarqueeRef}>
          <div className="pt-3 overflow-hidden">
            <div className="row justify-content-center">
              <div>
                <motion.span
                  className="text-primary d-block text-uppercase fw-semibold ls-xl text-center"
                  variants={fadeUp}
                  initial="hidden"
                  animate={partnersMarqueeInView ? "visible" : "hidden"}
                >
                  Trusted by top companies in India
                </motion.span>

                <div className="d-flex gap-1 justify-content-center mt-3 mb-4">
                  {[1, 2, 3].map((num) => (
                    <img
                      key={num}
                      src={`/assets/images/partners/${num}.png`}
                      className="marquee-image-partners bg-dark-soft px-5 py-3 rounded-4 opacity-100"
                      width="40"
                      alt=""
                    />
                  ))}
                </div>

                {/* <motion.div
                  className="mt-2"
                  variants={fadeIn}
                  initial="hidden"
                  animate={partnersMarqueeInView ? "visible" : "hidden"}
                >
                  <div className="marquee-wrap">
                    <div className="loop-track">
                      <div className="loop-collection">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <img
                            key={num}
                            src={`/assets/images/partners/${num}.png`}
                            className="marquee-image-partners bg-white px-5 py-3 rounded-4 opacity-100"
                            width="40"
                            alt=""
                          />
                        ))}
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <img
                            key={`dup-${num}`}
                            src={`/assets/images/partners/${num}.png`}
                            className="marquee-image-partners bg-white px-5 py-3 rounded-4 opacity-100"
                            width="40"
                            alt=""
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div> */}
              </div>
            </div>
          </div>
        </section>

        {/* ── Roadmap Section ─────────────────────────────────────────────── */}
        <section className="pb-7 mt-5 bg-light" ref={roadmapRef}>
          <div className="container pt-7">
            <motion.div
              className="row text-center"
              variants={fadeUp}
              initial="hidden"
              animate={roadmapInView ? "visible" : "hidden"}
            >
              <div className="col-12 mx-auto">
                <div className="d-flex flex-column gap-2 mb-4">
                  <h2 className="h1 mb-0 lh-1 ls-sm">
                    Your Roadmap to <u className="text-gold">Market Mastery</u>
                  </h2>
                  <p className="m-0 fs-4 lh-1">
                    At SLN Academy, we guide you step-by-step — from
                    understanding the basics to mastering the market with real
                    trading skills.
                  </p>
                  <div className="mt-n3 pt-2">
                    <img
                      src="/assets/images/line-shap.webp"
                      width="230"
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="row g-2"
              variants={staggerContainer}
              initial="hidden"
              animate={roadmapInView ? "visible" : "hidden"}
            >
              {benefits.map((item, index) => (
                <motion.div
                  key={index}
                  className="col-lg-3 col-md-4 col-6"
                  variants={fadeUp}
                  custom={index}
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <div className="card-hover-svg card rounded-5 card-body d-flex flex-column gap-3 text-center h-100 justify-content-center">
                    <div>
                      <motion.img
                        src={`/assets/images/icons/${index + 1}.svg`}
                        height="100"
                        alt={item.name}
                        whileHover={{ scale: 1.1 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      />
                    </div>
                    <div>
                      <h4 className="my-2 lh-1">{item.name}</h4>
                      <p className="text-gray-500 lh-sm m-0">{item.subtitle}</p>
                      <span className="badge bg-warning-soft rounded-pill fs-5 px-4 mt-4 mb-3">
                        Step {index + 1}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── Videos Section ──────────────────────────────────────────────── */}
        <div className="container" ref={videosRef}>
          <motion.div
            className="col-12 mt-7"
            variants={fadeUp}
            initial="hidden"
            animate={videosInView ? "visible" : "hidden"}
          >
            <span className="badge bg-dark-soft rounded-pill fs-5 px-4">
              Live <span className="fw-bold">Demo / Video</span>
            </span>
            <h2 className="fw-bold mb-0 text-dark mt-2">
              Stock market courses{" "}
              <u className="text-primary">
                <span className="text-dark">for everyone</span>
              </u>
            </h2>
            <p className="lead lh-sm">
              Choose from a wide range of stock market courses on trading and
              investing.
            </p>
          </motion.div>
          <motion.div
            className="row g-2 pt-3"
            variants={staggerContainer}
            initial="hidden"
            animate={videosInView ? "visible" : "hidden"}
          >
            {[
              "https://www.youtube.com/embed/CKN1h-aqVf8?si=dab78a-esRGkcF-_",
              "https://www.youtube.com/embed/2xa1kphLoyw?si=_4yVQpUodhJgSsR-",
            ].map((src, i) => (
              <motion.div
                key={i}
                className="col-lg-6 col-12"
                variants={fadeUp}
                custom={i}
                whileHover={{ scale: 1.015 }}
              >
                <iframe
                  height="320"
                  src={src}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="w-100 object-fit-cover rounded-5"
                ></iframe>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* ── Categories Section ──────────────────────────────────────────── */}
        <div className="container" ref={categoriesRef}>
          <motion.div
            className="row pt-4"
            variants={fadeUp}
            initial="hidden"
            animate={categoriesInView ? "visible" : "hidden"}
          >
            <div className="col-12">
              <h2 className="fw-bold mb-0 text-dark mt-2">
                Find courses by category
              </h2>
              <p className="lead lh-1 mb-4">
                Choose a course based on your stock market interests.
              </p>

              <motion.div
                className="row g-2"
                variants={staggerContainer}
                initial="hidden"
                animate={categoriesInView ? "visible" : "hidden"}
              >
                {category?.map((item, index) => (
                  <div className="col-3" key={index}>
                    <div className="card card-bordered shadow rounded-4 card-hover cursor-pointer h-100">
                      <div className="card-body d-flex align-items-center py-3">
                        <div>
                          <div className="d-md-flex">
                            <div className="">
                              <div class="icon-shape icon-md bg-light-primary rounded-circle">
                                <i class="bi bi-card-text fs-4 text-primary"></i>
                              </div>
                            </div>
                            <div className="ms-md-3 w-100">
                              <div className="d-flex justify-content-between">
                                <div>
                                  <h3 className="fs-4 m-0 lh-1">
                                    <Link
                                      to={`/category/${item?.category}`}
                                      className="text-dark"
                                    >
                                      {item?.category}
                                    </Link>
                                  </h3>
                                  <div>
                                    <span>{item?.count} Courses</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats Section ─────────────────────────────────────────────────── */}
      <section className="py-7 bg-gray mb-7" ref={statsRef}>
        <div className="container">
          <motion.div
            className="row text-center"
            variants={fadeUp}
            initial="hidden"
            animate={statsInView ? "visible" : "hidden"}
          >
            <div className="col-md-12 px-lg-8">
              <span className="text-uppercase text-dark fw-semibold">
                You turned SLN Academy into a success story
              </span>
              <h2 className="h1 fw-normal ls-sm text-success lh-1 mt-1">
                Together, the <span className="fw-bold">SLN Family</span> makes
                us stronger
              </h2>
              <div className="mt-n4 pt-2">
                <img
                  src="/assets/images/line-shap.webp"
                  width="230"
                  className="mt-2"
                />
              </div>
            </div>
          </motion.div>
          <div className="d-flex justify-content-center align-items-center">
            <div className="col-lg-10 col-12 mt-4">
              <motion.div
                className="row g-2 justify-content-center align-items-center"
                variants={staggerContainer}
                initial="hidden"
                animate={statsInView ? "visible" : "hidden"}
              >
                {[
                  {
                    bg: "bg-light-warning",
                    icon: "bi-award",
                    val: "500 +",
                    label: "Certified SLN Traders",
                  },
                  {
                    bg: "bg-light-info",
                    icon: "bi-bell",
                    val: "1 K+",
                    label: "Followers",
                  },
                  {
                    bg: "bg-light",
                    icon: "bi-person",
                    val: "15 +",
                    label: "Team Members",
                  },
                  {
                    bg: "bg-light-success",
                    icon: "bi-people",
                    val: "1 K+",
                    label: "SLN Trader",
                  },
                  {
                    bg: "bg-light-primary",
                    icon: "bi-play-circle",
                    val: "1 Thousand+",
                    label: "Youtube Subscribers",
                  },
                  {
                    bg: "bg-light-danger",
                    icon: "bi-download",
                    val: "1 K+",
                    label: "App Downloads",
                  },
                ].map(({ bg, icon, val, label }, i) => (
                  <motion.div
                    key={label}
                    className="col-lg-3 col-12"
                    variants={fadeUp}
                    custom={i}
                    whileHover={{ scale: 1.04 }}
                  >
                    <div className="pe-6 ps-2 py-2 rounded-pill bg-white">
                      <div className="d-flex align-items-center">
                        <div
                          className={`icon-shape icon-xl ${bg} rounded-circle`}
                        >
                          <i className={`bi ${icon} fs-3 text-primary`}></i>
                        </div>
                        <div className="ms-3">
                          <h3 className="mb-0 text-dark lh-1 fs-4">{val}</h3>
                          <span className="text-secondary">{label}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Success Story Section ─────────────────────────────────────────── */}
      <section className="mb-7" ref={successRef}>
        <div className="container">
          <motion.div
            className="row text-center"
            variants={fadeUp}
            initial="hidden"
            animate={successInView ? "visible" : "hidden"}
          >
            <div className="col-md-12 px-lg-8">
              <span className="text-uppercase text-secondary fw-semibold ls-sm">
                Alone we are smart. Together we are brilliant
              </span>
              <h2 className="h1 fw-semibold ls-sm text-success lh-1 mt-1">
                Our Success Story
              </h2>
              <div className="mt-n4 pt-2">
                <img
                  src="/assets/images/line-shap.webp"
                  width="230"
                  className="mt-2"
                />
              </div>
            </div>
          </motion.div>
          <motion.div
            className="d-flex justify-content-center mt-5"
            variants={scaleIn}
            initial="hidden"
            animate={successInView ? "visible" : "hidden"}
          >
            <div className="col-lg-7 col-12">
              <iframe
                height="320"
                src="https://www.youtube.com/embed/CKN1h-aqVf8?si=dab78a-esRGkcF-_"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="w-100 object-fit-cover rounded-5"
              ></iframe>
            </div>
          </motion.div>
        </div>
        <div className="bg-warning p-8 mt-n10">
          <div className="p-8 m-2"></div>
        </div>
      </section>

      {/* ── Testimonials Section ──────────────────────────────────────────── */}
      <section className="mb-7" ref={testimonialRef}>
        <div className="container">
          <motion.div
            className="row mb-5 text-center"
            variants={fadeUp}
            initial="hidden"
            animate={testimonialInView ? "visible" : "hidden"}
          >
            <div className="col-md-12 px-lg-8">
              <span className="text-uppercase text-secondary fw-semibold ls-sm">
                TESTIMONIAL
              </span>
              <h2 className="h1 fw-semibold ls-sm text-success lh-1 mt-1">
                That's What They Said
              </h2>
              <div className="mt-n3 pt-2">
                <img
                  src="/assets/images/line-shap.webp"
                  width="230"
                  className="mt-2"
                />
              </div>
            </div>
          </motion.div>
          <div className="row">
            <motion.div
              className="col-lg-4 col-12"
              variants={slideLeft}
              initial="hidden"
              animate={testimonialInView ? "visible" : "hidden"}
            >
              <div>
                <span className="text-uppercase text-secondary fw-semibold ls-sm">
                  HAPPY FACES
                </span>
                <h2 className="h1 fw-semibold ls-sm text-black lh-1 mt-1">
                  SLN Success Stories
                </h2>
                <p className="lead lh-sm mb-4">
                  Welcome, to the leading best Stock market Institute, we offer
                  Stock market courses for future growth and impart deep
                  knowledge of the stock market and Trading.
                </p>
                <motion.div
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  style={{ display: "inline-block" }}
                >
                  <Link
                    to="/"
                    onClick={openLoginModal}
                    className="btn border-1 border-dark fw-normal w-auto me-2 px-5 me-lg-0 mb-lg-0 mb-4 rounded-pill"
                  >
                    Create Account
                  </Link>
                </motion.div>
              </div>
            </motion.div>
            <motion.div
              className="col-lg-8 col-12"
              variants={slideRight}
              initial="hidden"
              animate={testimonialInView ? "visible" : "hidden"}
            >
              <div className="row g-0">
                <div className="col-4">
                  <div className="h-100">
                    <img
                      src="/assets/images/home/5.jpg"
                      alt="mentor"
                      className="w-100 object-fit-cover rounded-5"
                      height="260"
                    />
                  </div>
                </div>
                <div className="col-8 px-4 d-flex align-items-center pb-4 pt-1">
                  <motion.div
                    ref={bannerRef}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={bannerInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                    id="testimonialSlider"
                    className="carousel slide w-100"
                    data-bs-ride="carousel"
                  >
                    <div className="carousel-inner">
                      {testimonials.length > 0 ? (
                        testimonials.map((t, i) => (
                          <div
                            key={t.reviewId || t.id || i}
                            className={`carousel-item ${i === 0 ? "active" : ""}`}
                          >
                            <div className="d-flex flex-column px-2 mb-2 h-100">
                              <div className="d-flex flex-column gap-3 mt-4">
                                <div className="d-flex flex-column">
                                  <p className="mb-0 lh-sm mt-1 mb-2 fs-4">
                                    {t.review}
                                  </p>
                                  <div className="mt-2">
                                    <span className="text-black fs-3 m-0 lh-1 mt-2 fw-medium ls-1">
                                      {t.userName || "Student"}
                                    </span>
                                    <p className="mb-0 lh-sm m-0 fs-4">
                                      {t.courseName}
                                    </p>
                                  </div>
                                </div>
                                <div className="lh-1">
                                  <span className="text-secondary fw-semibold">
                                    {t.rating}.0
                                  </span>
                                  <span className="align-text-top ms-2">
                                    {[...Array(5)].map((_, j) => (
                                      <i
                                        key={j}
                                        className={`bi bi-star${j < t.rating ? "-fill" : ""} text-warning me-1`}
                                      />
                                    ))}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="carousel-item active">
                          <div className="d-flex flex-column px-2 mb-2 h-100">
                            <div className="d-flex flex-column gap-3 mt-4">
                              <div className="d-flex flex-column">
                                <p className="mb-0 lh-sm mt-1 mb-2 fs-4">
                                  I was a beginner with zero knowledge, but the
                                  way concepts are explained here is very simple
                                  and practical. The live market examples helped
                                  me gain real confidence.
                                </p>
                                <div className="mt-2">
                                  <span className="text-black fs-3 m-0 lh-1 mt-2 fw-medium ls-1">
                                    Rahul Kumar
                                  </span>
                                  <p className="mb-0 lh-sm m-0 fs-4">
                                    SLN Academy Course
                                  </p>
                                </div>
                              </div>
                              <div className="lh-1">
                                <span className="text-secondary fw-semibold">
                                  4.5
                                </span>
                                <span className="align-text-top ms-2">
                                  {[...Array(5)].map((_, i) => (
                                    <i
                                      key={i}
                                      className="bi bi-star-fill text-warning me-1"
                                    />
                                  ))}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      className="carousel-control-prev"
                      type="button"
                      data-bs-target="#testimonialSlider"
                      data-bs-slide="prev"
                      style={{ bottom: "-230px" }}
                    >
                      <div className="button-md-circle">
                        <i className="bi bi-chevron-left display-5 text-dark"></i>
                      </div>
                    </button>
                    <button
                      className="carousel-control-next"
                      type="button"
                      data-bs-target="#testimonialSlider"
                      data-bs-slide="next"
                      style={{ bottom: "-230px" }}
                    >
                      <div className="button-md-circle">
                        <i className="bi bi-chevron-right display-5 text-dark"></i>
                      </div>
                    </button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Tabs Section ──────────────────────────────────────────────────── */}
      <section className="bg-white pb-7" ref={tabsRef}>
        <motion.div
          className="container"
          variants={fadeUp}
          initial="hidden"
          animate={tabsInView ? "visible" : "hidden"}
        >
          <div className="row">
            <div className="col-md-12">
              <ul
                className="nav nav-lb-tab mb-4 bg-gray-200 px-6 rounded-4"
                id="pills-tab"
                role="tablist"
              >
                {["Key Highlights", "Problems", "Solutions"].map((tab, i) => {
                  const ids = [
                    "pills-development",
                    "pills-design",
                    "pills-marketing",
                  ];
                  return (
                    <li key={tab} className="nav-item mx-0" role="presentation">
                      <a
                        className={`nav-link px-5 ${i === 0 ? "active" : ""}`}
                        id={`${ids[i]}-tab`}
                        data-bs-toggle="pill"
                        href={`#${ids[i]}`}
                        role="tab"
                      >
                        {tab}
                      </a>
                    </li>
                  );
                })}
              </ul>
              <div className="tab-content" id="pills-tabContent">
                <div
                  className="tab-pane fade show active"
                  id="pills-development"
                  role="tabpanel"
                >
                  <div className="row g-2">
                    <div className="col-lg-6 col-12 pe-7 d-flex align-items-center">
                      <div>
                        <h4 className="text-dark">Empowering Future Traders</h4>
                        <p className="text-secondary lh-sm">
                          A DPIIT-recognized fintech education startup training
                          5,000+ students through hybrid courses, YouTube
                          content, and institutional tie-ups, with strong career
                          outcomes and growing market potential.
                        </p>
                        <ol className="pe-7 lh-sm">
                          <li>
                            DPIIT-recognized Startup India fintech education
                            platform based in Prayagraj, Uttar Pradesh.
                          </li>
                          <li>
                            5000+ students trained through free and paid
                            programs.
                          </li>
                          <li>
                            40+ successful job placements via partner brokers.
                          </li>
                          <li>
                            5,000+ YouTube subscribers and 700+ videos as part
                            of the freemium-to-paid conversion model.
                          </li>
                          <li>
                            Strong market potential with 14+ crore demat
                            accounts in India and rising demand for certified
                            traders.
                          </li>
                          <li>
                            Hybrid training model (live + recorded) ensuring
                            accessibility and scalability.
                          </li>
                        </ol>
                        <div className="mt-5 mb-lg-0 mb-3">
                          <Link
                            to="/"
                            onClick={openLoginModal}
                            className="btn border-1 border-dark fw-normal w-auto me-2 px-5 me-lg-0 rounded-pill"
                          >
                            Create Account
                          </Link>
                          <Link
                            to="/course/CREA830F/free-masterclass"
                            className="btn bg-gold text-dark fw-normal w-auto me-2 px-5 me-lg-0 rounded-pill ms-2"
                          >
                            Join Free Masterclass
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6 col-12">
                      <iframe
                        height="350"
                        src="https://www.youtube.com/embed/kyumSEho45Q?si=uWYMXp90OzEeMM67"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        className="w-100 object-fit-cover rounded-5"
                      ></iframe>
                    </div>
                  </div>
                </div>
                <div
                  className="tab-pane fade"
                  id="pills-design"
                  role="tabpanel"
                >
                  <div className="row g-2">
                    <div className="col-lg-6 col-12 pe-7 d-flex align-items-center">
                      <div>
                        <h4 className="text-dark">
                          Gaps in Stock Market Education
                        </h4>
                        <p className="text-secondary lh-sm">
                          Most aspiring traders lack structured, affordable, and
                          career-focused training, with limited access to
                          mentors, practical exposure, and industry-backed
                          opportunities.
                        </p>
                        <ol className="pe-7 lh-sm">
                          <li>
                            Large segment of Indian youth unaware of structured
                            career paths in stock market trading and advisory
                            roles.
                          </li>
                          <li>
                            Majority of existing courses are purely theoretical
                            with limited or no practical exposure.
                          </li>
                          <li>
                            Lack of recognized certifications in many training
                            programs.
                          </li>
                          <li>
                            No job placement assistance in most available stock
                            market education offerings.
                          </li>
                          <li>
                            Significant gap in bridging passion for trading with
                            professional career opportunities.
                          </li>
                          <li>
                            Limited accessibility in Tier-2 & Tier-3 cities,
                            where demand for such training is growing.
                          </li>
                        </ol>
                        <div className="mt-5">
                          <Link
                            to="/about"
                            className="btn border-1 border-dark fw-normal w-auto me-2 px-5 me-lg-0 rounded-pill"
                          >
                            About Us
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6 col-12">
                      <img
                        src="/assets/images/home/8.jpg"
                        className="object-fit-cover w-100 rounded-5"
                        height="350"
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="tab-pane fade"
                  id="pills-marketing"
                  role="tabpanel"
                >
                  <div className="row g-2">
                    <div className="col-lg-6 col-12 pe-7 d-flex align-items-center">
                      <div>
                        <h4 className="text-dark">
                          Accessible, Scalable Learning
                        </h4>
                        <p className="text-secondary lh-sm">
                          We bridge the gap with hybrid training programs,
                          industry partnerships for placements, freemium-to-paid
                          learning pathways, and tailored courses designed to
                          make stock market education affordable and impactful.
                        </p>
                        <ol className="pe-7 lh-sm">
                          <li>
                            3 & 6 - months Practical Trading Diploma covering
                            intraday, swing, and options trading with hands-on
                            learning.
                          </li>
                          <li>
                            Job-ready certifications like NISM XA, XB, and VIII
                            integrated into the curriculum.
                          </li>
                          <li>
                            Live online classes combined with recorded sessions
                            for flexible, hybrid learning.
                          </li>
                          <li>
                            Practice tests and resume support to prepare
                            students for industry roles.
                          </li>
                          <li>
                            Job placement assistance through partnerships with
                            broker firms.
                          </li>
                          <li>
                            Affordable pricing with freemium YouTube content to
                            attract and convert learners.
                          </li>
                          <li>
                            Accessible nationwide, especially in Tier-2 & Tier-3
                            cities, through online delivery.
                          </li>
                        </ol>
                        <div className="mt-5">
                          <Link
                            to="/category"
                            className="btn border-1 border-dark fw-normal w-auto me-2 px-5 me-lg-0 rounded-pill"
                          >
                            Explore
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6 col-12">
                      <img
                        src="/assets/images/home/7.jpg"
                        className="object-fit-cover w-100 rounded-5"
                        height="350"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── Mission Banner ────────────────────────────────────────────────── */}
      <section className="bg-success" ref={missionRef}>
        <div className="container">
          <motion.div
            className="py-6 rounded-5"
            variants={fadeUp}
            initial="hidden"
            animate={missionInView ? "visible" : "hidden"}
          >
            <div className="container">
              <div className="row">
                <motion.div
                  className="col-lg-8"
                  variants={slideLeft}
                  initial="hidden"
                  animate={missionInView ? "visible" : "hidden"}
                >
                  <h5 className="mb-0 fs-3 fw-normal lh-sm text-light text-uppercase">
                    From learning to earning — we aim to create confident
                    traders
                  </h5>
                  <h2 className="h1 text-white fw-normal ls-sm lh-1">
                    A Trader in Every{" "}
                    <span className="fw-bold">Indian Home</span>
                  </h2>
                  <div className="mt-n4 pt-2">
                    <img
                      src="/assets/images/line-shap.webp"
                      width="230"
                      className="mt-2"
                    />
                  </div>
                </motion.div>
                <motion.div
                  className="col-lg-4 d-flex justify-content-center justify-content-lg-end align-items-center"
                  variants={fadeUp}
                  initial="hidden"
                  animate={missionInView ? "visible" : "hidden"}
                >
                  <motion.p
                    className="text-stroke m-0 display-1"
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={missionInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
                  >
                    10,000+
                  </motion.p>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Telegram CTA ─────────────────────────────────────────────────── */}
      <section className="bg-white pb-2" ref={telegramRef}>
        <div className="container">
          <motion.div
            className="bg-black rounded-5 mt-7 overflow-hidden"
            variants={scaleIn}
            initial="hidden"
            animate={telegramInView ? "visible" : "hidden"}
          >
            <div className="row align-items-center gy-5 gy-xl-0">
              <motion.div
                className="col-lg-6 col-12"
                variants={slideLeft}
                initial="hidden"
                animate={telegramInView ? "visible" : "hidden"}
              >
                <div className="d-flex flex-column gap-4 ps-lg-8 p-6">
                  <div className="d-flex flex-column gap-3">
                    <h1 className="mb-0 display-3 fw-bold text-white lh-1">
                      Get Our Ideas
                    </h1>
                    <p className="mb-0 pe-xxl-8 me-xxl-5 fs-3 lh-sm">
                      Receive daily tips, strategies, and exclusive updates
                      directly on your Telegram.
                    </p>
                  </div>
                  <div>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.96 }}
                      style={{ display: "inline-block" }}
                    >
                      <a
                        href="https://www.youtube.com/@SLNAcademyOfficial"
                        target="_blank"
                        className="btn bg-white text-black fw-normal fs-3 w-auto mt-2 px-6 me-lg-0 rounded-4"
                      >
                        Join Us On YouTube
                      </a>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                className="col-lg-6 col-12 d-none d-lg-block"
                variants={slideRight}
                initial="hidden"
                animate={telegramInView ? "visible" : "hidden"}
              >
                <div className="text-center position-relative mt-2">
                  <img
                    src="https://geeksui.codescandy.com/geeks/assets/images/course/woman-hero.png"
                    alt="hero"
                    height="300"
                    className="position-relative z-3"
                  />
                  <div className="position-absolute top-0 end-0 bottom-0">
                    <img
                      src="https://geeksui.codescandy.com/geeks/assets/images/course/side-shape.svg"
                      alt=""
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Institution Partners Section ──────────────────────────────────── */}
      <section className="my-6 pb-3" ref={institutionPartnersRef}>
        <div className="container">
          <div className="d-flex justify-content-between mb-5">
            <motion.div
              variants={slideLeft}
              initial="hidden"
              animate={institutionPartnersInView ? "visible" : "hidden"}
            >
              <span className="text-uppercase text-secondary fw-semibold ls-sm">
                OUR PARTNERS
              </span>
              <h2 className="h1 fw-semibold ls-sm text-success lh-1 mt-1">
                Collaborating With India's Leading
                <br />
                Financial Institutions
              </h2>
              <div className="mt-n3 pt-2">
                <img
                  src="/assets/images/line-shap.webp"
                  width="230"
                  className="mt-2"
                />
              </div>
            </motion.div>

            <motion.div
              className="d-lg-block d-none"
              variants={slideRight}
              initial="hidden"
              animate={institutionPartnersInView ? "visible" : "hidden"}
            >
              <div className="mt-3 gap-1 d-flex justify-content-end align-items-center">
                {[
                  "Bajaj Broking",
                  "ICICI Direct",
                  "Kotak Securities",
                  "Zerodha",
                  "Upstox",
                ].map((option, index) => (
                  <motion.div
                    key={index}
                    className={`rounded-pill py-2 px-4 mb-2 d-inline-block cursor-pointer ${index === 0 ? "bg-black" : "bg-light"}`}
                    whileHover={{ scale: 1.07 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span
                      className={`font-semibold mx-1 ${index === 0 ? "text-white" : "text-gray-800"}`}
                    >
                      {option}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div
            className="d-flex gap-2 overflow-x-scroll"
            variants={staggerContainer}
            initial="hidden"
            animate={institutionPartnersInView ? "visible" : "hidden"}
          >
            {["1.jpeg", "2.jpeg", "3.jpeg"].map((img, i) => (
              <motion.img
                key={img}
                src={`/assets/images/third/${img}`}
                className="object-fit-cover rounded-5 border border-light"
                width="260"
                variants={fadeUp}
                custom={i}
                whileHover={{ scale: 1.04 }}
                transition={{ type: "spring", stiffness: 200 }}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Contact / Call Back Section ───────────────────────────────────── */}
      <section className="pb-8 bg-white" ref={contactRef}>
        <div className="container mt-7">
          <div className="row">
            <motion.div
              className="col-xl-6 col-lg-6 col-md-12 d-flex align-items-center"
              variants={slideLeft}
              initial="hidden"
              animate={contactInView ? "visible" : "hidden"}
            >
              <div className="mb-4 mb-xl-0 text-center text-md-start">
                <span className="badge bg-danger-soft rounded-pill fs-5 px-4 mb-3">
                  Contact Us
                </span>
                <h1 className="display-4 fw-bold mb-3 ls-sm lh-1">
                  Get a Call Back
                </h1>
                <p className="mb-3 text-black lead lh-sm">
                  Have questions? Just share your contact — our experts will
                  call you and help you get started in no time!
                </p>
                <p className="mb-3 lead lh-sm">
                  Swinglearn bridges the gap between passion and profession in
                  stock trading by combining practical training, certifications,
                  and placement support. The platform has already reached 5,000+
                  learners.
                </p>
                <div className="mb-4 mb-0">
                  <ul className="list-unstyled fs-4">
                    {["Live Courses", "Recorded Courses", "Free Courses"].map(
                      (item) => (
                        <li key={item}>
                          <span className="me-2">
                            <i className="bi bi-star text-warning" />
                          </span>
                          <span className="align-text-top">{item}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
                <div>
                  <Link
                    className="btn btn-outline-dark rounded-pill px-5 btn-md mt-3 mb-lg-0 mb-5 me-2"
                    onClick={openLoginModal}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    Join SLN Academy
                  </Link>
                  <Link
                    className="btn btn-dark px-5 rounded-pill btn-md mt-3 mb-lg-0 mb-5"
                    to="/about"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    Know More
                  </Link>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="offset-xxl-1 col-xxl-5 col-lg-6 col-md-12"
              variants={slideRight}
              initial="hidden"
              animate={contactInView ? "visible" : "hidden"}
            >
              <div className="card smooth-shadow-md rounded-5 mt-lg-0 mt-4">
                <div className="card-body px-6 pt-5 pb-4">
                  <form className="needs-validation">
                    <div className="mb-2">
                      <input
                        type="email"
                        id="courseLeadEmail"
                        className="form-control rounded-pill px-4"
                        placeholder="Full Name"
                      />
                    </div>
                    <div className="mb-2">
                      <input
                        type="email"
                        id="courseLeadEmail2"
                        className="form-control rounded-pill px-4"
                        placeholder="Phone No"
                      />
                    </div>
                    <div className="mb-2">
                      <input
                        type="email"
                        id="courseLeadEmail3"
                        className="form-control rounded-pill px-4"
                        placeholder="Email"
                      />
                    </div>
                    <div className="mb-2">
                      <input
                        type="password"
                        id="courseLeadPassword"
                        className="form-control rounded-pill px-4"
                        placeholder="Subject"
                      />
                    </div>
                    <div className="mb-2">
                      <textarea
                        type="text"
                        className="form-control rounded-4 px-4"
                        placeholder="Complete Address"
                        rows={2}
                        defaultValue={""}
                      />
                    </div>
                    <div className="d-grid">
                      <motion.button
                        type="submit"
                        className="btn btn-primary rounded-pill"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        Send Message
                      </motion.button>
                    </div>
                  </form>
                </div>
                <div className="card-footer rounded-5 px-6 py-4">
                  <p className="mb-0 text-center">
                    By continuing you accept the{" "}
                    <a
                      href="#"
                      className="text-dark fw-semibold text-decoration-underline"
                    >
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default Home;
